/* ============================================================
   SHIVOO STUDIOS — immersive scroll engine
   Single persistent WebGL canvas. One camera. Scene modules that
   fade/scrub based on scroll. Light "putty" studio aesthetic.
   ============================================================ */
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import { TTFLoader } from 'three/examples/jsm/loaders/TTFLoader.js';
// fat dashed lines for the comms arcs (LineBasicMaterial linewidth is ignored)
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';

// asset paths must respect Vite's base (prod lives under /2026industrial/)
const asset = (p) => import.meta.env.BASE_URL + p.replace(/^\//, '');

// real Space Grotesk (brand font) for the extruded hat words
let SG_FONT = null;
const fontReady = new Promise((resolve) => {
  new TTFLoader().load(asset('fonts/SpaceGrotesk-500.ttf'), (json) => {
    SG_FONT = new FontLoader().parse(json);
    resolve(SG_FONT);
  }, undefined, () => resolve(null));
});

// Ze'ev's AE cube choreography, converted to three-space keyframes (cube-local
// units, normalised time). Timing comes from the keys; interpolation is ours.
import cubeAnim from './cube-anim.json';
const _qa = new THREE.Quaternion(), _qb = new THREE.Quaternion();
const _wq1 = new THREE.Quaternion(), _wq2 = new THREE.Quaternion();
const _wv1 = new THREE.Vector3(), _wv2 = new THREE.Vector3();
const _wx = new THREE.Vector3(1, 0, 0), _wy = new THREE.Vector3(0, 1, 0);
function sampleTrack(track, u, pos, quat) {
  let lo = 0, hi = track.length - 1;
  if (u <= track[0].t) hi = 1;
  else if (u >= track[hi].t) lo = hi - 1;
  else { while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (track[mid].t <= u) lo = mid; else hi = mid; } }
  const a = track[lo], b = track[hi];
  const f = clamp((u - a.t) / ((b.t - a.t) || 1), 0, 1);
  if (pos) pos.set(a.p[0] + (b.p[0] - a.p[0]) * f, a.p[1] + (b.p[1] - a.p[1]) * f, a.p[2] + (b.p[2] - a.p[2]) * f);
  if (quat) { _qa.set(a.q[0], a.q[1], a.q[2], a.q[3]); _qb.set(b.q[0], b.q[1], b.q[2], b.q[3]); quat.slerpQuaternions(_qa, _qb, f); }
}

/* ---------- palette ---------- */
const COL = {
  ink:    0x232624,
  putty:  0xE6E1D7,
  light:  0xF4F2EE,
  accent: 0xC8552E,
  brass:  0xB98A4B,
  puttyDark: 0x57534B,   // solid shell the photo cube resolves into
  warm:   0xECE6DA,
  cool:   0xCDC8BC,
};

/* ---------- math helpers ---------- */
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const lerp = (a, b, t) => a + (b - a) * t;
const smooth = (t) => { t = clamp(t, 0, 1); return t * t * (3 - 2 * t); };
const smoother = (t) => { t = clamp(t, 0, 1); return t * t * t * (t * (t * 6 - 15) + 10); };  // quintic ease
// map x in [a,b] -> [0,1]
const range = (x, a, b) => clamp((x - a) / (b - a), 0, 1);
// seeded pseudo-random for stable scatter
function mulberry32(seed) {
  return function () {
    seed |= 0; seed = (seed + 0x6D2B79F5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* ---------- loaders ---------- */
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath(asset('draco/'));
const gltfLoader = new GLTFLoader();
gltfLoader.setDRACOLoader(dracoLoader);
const texLoader = new THREE.TextureLoader();

function loadGLB(path, onFrac) {
  return new Promise((resolve, reject) => {
    gltfLoader.load(path, resolve, (xhr) => {
      if (onFrac && xhr.total) onFrac(xhr.loaded / xhr.total);
    }, reject);
  });
}
function loadTex(path) {
  return new Promise((resolve, reject) => {
    texLoader.load(path, (t) => { t.colorSpace = THREE.SRGBColorSpace; resolve(t); }, undefined, reject);
  });
}

/* ---------- model utilities ---------- */
// center a loaded object at origin and scale so its largest dim == target
function normalize(obj, target = 3, axisBias = null) {
  const box = new THREE.Box3().setFromObject(obj);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const maxDim = axisBias === 'x' ? size.x : Math.max(size.x, size.y, size.z) || 1;
  const s = target / maxDim;
  obj.scale.setScalar(s);
  // position is applied AFTER scale in the matrix, so the recentre offset
  // must itself be scaled — otherwise models authored off-origin fly away.
  obj.position.copy(center).multiplyScalar(-s);
  return { size, center, scale: s };
}
// collect materials (kept for per-piece opacity control)
function collectMaterials(root, store) {
  root.traverse((o) => {
    if (o.isMesh && o.material) {
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      mats.forEach((m) => {
        m.transparent = true;
        store.push({ m, base: m.opacity != null ? m.opacity : 1 });
      });
    }
  });
}
// assign temp materials by node/mesh name keyword (untextured models)
function recolorByName(root, rules, fallback, transparent = false) {
  root.traverse((o) => {
    if (!o.isMesh) return;
    const name = ((o.name || '') + ' ' + (o.parent ? o.parent.name : '')).toLowerCase();
    let spec = fallback;
    for (const r of rules) { if (r.keys.some((k) => name.includes(k))) { spec = r; break; } }
    o.material = new THREE.MeshStandardMaterial({
      color: spec.color,
      metalness: spec.metalness != null ? spec.metalness : 0.4,
      roughness: spec.roughness != null ? spec.roughness : 0.5,
      transparent,
      opacity: 1,
    });
  });
}

/* ============================================================
   Scene base class
   ============================================================ */
class Scene {
  constructor(engine, sectionId) {
    this.engine = engine;
    this.section = document.getElementById(sectionId);
    this.rail = new THREE.Group();     // vertical scroll wrapper (slides up/off the top)
    this.group = new THREE.Group();    // scene content
    this.rail.add(this.group);
    this.rail.visible = false;
    engine.stage.add(this.rail);
    this.mats = [];
    this.loaded = false;
    this.loading = false;
    this.opacity = 1;                  // scenes are never cross-faded now; rail handles transitions
    this.noRail = false;               // earth opts out (zoom-out instead of slide)
  }
  async ensureLoaded() {
    if (this.loaded || this.loading) return;
    this.loading = true;
    try { await this.load(); } catch (e) { console.error('scene load failed', e); }
    this.loaded = true;
  }
  // overrides
  async load() {}
  update() {}
}

/* ============================================================
   Scene 1 — Industrial Arm  (panels 1-3)
   ============================================================ */
class ArmScene extends Scene {
  constructor(engine, sectionId) {
    super(engine, sectionId);
    this.enterFrom = -0.55;   // start rising while still on the hero
    this.enterH = 4.2;        // peek just under the fold, not fully below
  }
  async load() {
    const gltf = await loadGLB(asset('models/compressed/IndustrialArm.glb'),
      (f) => this.engine.setProg('arm', f));
    this.model = gltf.scene;
    normalize(this.model, 2.8);
    this.model.position.y -= 0.1;
    collectMaterials(this.model, this.mats);
    this.group.add(this.model);
    this.group.rotation.y = -2.0;  // face left
    this.group.position.x = 1.2;   // sit right of the left-aligned copy, facing it
    if (gltf.animations && gltf.animations.length) {
      this.mixer = new THREE.AnimationMixer(this.model);
      this.clip = gltf.animations.find((a) => a.name === 'Scene') || gltf.animations[0];
      this.action = this.mixer.clipAction(this.clip);
      this.action.play();
      this.action.paused = true;
    }

    // extruded Space Grotesk words, pulled out of the hat AFTER it flips
    this.hat = this.model.getObjectByName('Hat_LP');
    const font = await fontReady;
    this.words = [];
    if (font) {
      const defs = [
        { text: 'SIMULATION', win: [0.46, 0.60], accent: true },
        { text: 'SUPPORT',    win: [0.56, 0.70], accent: false },
        { text: 'TRAINING',   win: [0.66, 0.80], accent: true },
        { text: 'MARKETING',  win: [0.76, 0.90], accent: false },
      ];
      defs.forEach((d) => {
        const geo = new TextGeometry(d.text, {
          font, size: 0.12, depth: 0.025, curveSegments: 4,
          bevelEnabled: true, bevelThickness: 0.003, bevelSize: 0.0015, bevelSegments: 1,
        });
        geo.center();
        const mat = new THREE.MeshStandardMaterial({
          color: d.accent ? COL.accent : COL.ink, metalness: 0.25, roughness: 0.5, transparent: true, opacity: 1,
        });
        const mesh = new THREE.Mesh(geo, mat);
        mesh.userData.win = d.win;
        mesh.visible = false;
        this.words.push(mesh);
        this.group.add(mesh);
      });
    }
    this._q = new THREE.Quaternion();
  }
  update(t, dt, time, mx, my, raw) {
    // animation: skip the first 4s of idle; play from 4s as the arm enters the
    // viewport so it's mid-action by the time it's centred, finishing the hat flip
    if (this.action && this.clip) {
      const animT = range(raw, -0.30, 0.45);
      this.action.time = lerp(4.0, this.clip.duration, animT);
      this.mixer.update(0);
    }
    this.group.rotation.y = -2.0 - t * 0.4;
    this.group.position.y = -0.1;   // fixed — no idle bob

    // small words pulled out of the hat: emerge from the hole, rotate to face the
    // camera so they're readable, then drift up a little and fade — one per scroll
    if (this.hat && this.words && this.words.length) {
      this.model.updateMatrixWorld(true);
      const hatPos = this.hat.getWorldPosition(new THREE.Vector3());
      const hatQuat = this.hat.getWorldQuaternion(this._q.clone());
      const openWorld = new THREE.Vector3(0, -1, 0).applyQuaternion(hatQuat).normalize();
      const gqi = this.group.getWorldQuaternion(new THREE.Quaternion()).invert();
      const basePos = this.group.worldToLocal(hatPos.clone());
      const openLocal = openWorld.clone().applyQuaternion(gqi).normalize();
      const upLocal = new THREE.Vector3(0, 1, 0).applyQuaternion(gqi).normalize();
      const camPosLocal = this.group.worldToLocal(this.engine.camera.position.clone());
      const camQuatLocal = gqi.clone().multiply(this.engine.camera.quaternion);
      // emerge mostly upward (with a hint of the hole's tilt) so the word rises
      // just above the hat and stays readable, instead of drifting out of frame
      const emergeDir = openLocal.clone().multiplyScalar(0.4).add(upLocal).normalize();
      this.words.forEach((w) => {
        const p = range(t, w.userData.win[0], w.userData.win[1]);
        if (p <= 0.001) { w.visible = false; return; }
        w.visible = true;
        const emerge = smooth(range(p, 0.0, 0.30));   // come up out of the hole
        // gentle, gradual turn: begins almost as soon as the word leaves the hole,
        // spread across the whole rise, never reaching full 90° (caps at ~80%)
        const face = smooth(range(p, 0.08, 0.85)) * 0.8;
        const pos = basePos.clone()
          .addScaledVector(emergeDir, emerge * 0.35)
          .addScaledVector(upLocal, p * 1.3);          // continuous rise — never pauses
        w.position.copy(pos);
        w.scale.setScalar(0.45 + emerge * 0.6);
        // orientation: lying along the hole -> billboard upright (readable)
        const xAxis = openLocal.clone();
        const camDir = camPosLocal.clone().sub(pos).normalize();
        const zAxis = new THREE.Vector3().crossVectors(new THREE.Vector3().crossVectors(xAxis, camDir), xAxis).normalize();
        const yAxis = new THREE.Vector3().crossVectors(zAxis, xAxis).normalize();
        const rotA = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(xAxis, yAxis, zAxis));
        w.quaternion.copy(rotA).slerp(camQuatLocal, face);
        w.material.opacity = 1 - smooth(range(p, 0.86, 1.0));   // fade only at the very end
      });
    }
  }
}

/* ============================================================
   Scene 2 — Nanotech digital twin  (panel 4)
   Flat photos float in confusion, then converge and resolve
   into the single 3D machine.
   ============================================================ */
class NanotechScene extends Scene {
  constructor(engine, sectionId) {
    super(engine, sectionId);
    // Ze'ev's AE grid pose is TOP-heavy (top row centre y=1.62, +0.675 half
    // plane = +2.3 above the group origin). The default enterH (3.7) leaves
    // that row already inside the frame (bottom edge ≈ −2.72 at the grid's
    // depth) when the rail becomes visible — it CUT IN instead of scrolling
    // in. Start deep enough that the whole grid is below the fold, with
    // margin for the mouse-tilt (±1.0 at this depth at the 2x tilt).
    this.enterH = 6.5;
  }
  async load() {
    // 6 square-cropped photographs: boring flat grid -> fold into a closed cube
    // -> the box opens (sides fall away on hinges) revealing the machine inside
    this.planes = [];
    const texPaths = [];
    for (let i = 1; i <= 6; i++) texPaths.push(asset(`images/2600-${i}.jpg`));
    const texes = await Promise.all(texPaths.map((p, i) =>
      loadTex(p).then((t) => { this.engine.setProg('img' + i, 1); return t; })));
    const S = 1.35, hf = S / 2;
    // grid slot i (UL,UM,UR / LL,LM,LR) -> the face that photo becomes in the AE comp
    const roles = ['back', 'top', 'front', 'left', 'bottom', 'right'];
    this.cube = new THREE.Group();
    this.group.add(this.cube);
    this.nullTrack = cubeAnim.null;
    // every photo's BACK is solid dark putty (ONE shared material): the faces
    // fold to look into the cube interior, so their putty backs progressively
    // become the cube's exterior — no crossfade
    this.backMat = new THREE.MeshLambertMaterial({ color: COL.puttyDark, side: THREE.BackSide });
    const rand = mulberry32(5);
    texes.forEach((tex, i) => {
      // centred square crop
      const iw = tex.image.width, ih = tex.image.height;
      if (iw > ih) { tex.repeat.set(ih / iw, 1); tex.offset.set((1 - ih / iw) / 2, 0); }
      else { tex.repeat.set(1, iw / ih); tex.offset.set(0, (1 - iw / ih) / 2); }
      const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: 1, side: THREE.FrontSide });
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(S, S), mat);
      // coplanar putty plane, BackSide: face culling splits the pair cleanly
      // (photo from the front, putty from behind — explosion tumbles show it)
      mesh.add(new THREE.Mesh(new THREE.PlaneGeometry(S, S), this.backMat));
      const role = roles[i];
      mesh.userData.role = role;
      const track = cubeAnim.faces[role];
      const last = track[track.length - 1];
      mesh.userData.track = track;
      mesh.userData.facePos = new THREE.Vector3(last.p[0], last.p[1], last.p[2]);
      mesh.userData.faceQuat = new THREE.Quaternion(last.q[0], last.q[1], last.q[2], last.q[3]);
      // the front face flies with a 180° local-Y flip baked in (see update) —
      // keep the explosion handoff pose consistent with that
      if (role === 'front')
        mesh.userData.faceQuat.multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI));
      // slow-explosion exit: each face flies out along its outward normal;
      // the back face gets an up bias (straight -z would just shrink in place)
      // and the front face drops so it clears the frame instead of swallowing it
      const dir = mesh.userData.facePos.clone().normalize();
      if (dir.z < -0.5) dir.add(new THREE.Vector3(0, 1.4, 0));
      if (dir.z > 0.5) dir.add(new THREE.Vector3(0, -1.2, 0));
      mesh.userData.flyDir = dir.normalize();
      mesh.userData.tumbleAxis = new THREE.Vector3(rand() - 0.5, rand() - 0.5, rand() - 0.5).normalize();
      mesh.userData.tumbleSpin = (0.8 + rand() * 0.8) * (i % 2 ? 1.6 : -1.6);
      sampleTrack(track, 0, mesh.position, mesh.quaternion);   // start in Ze'ev's grid pose
      this.planes.push(mesh);
      this.cube.add(mesh);
    });

    // the resolved 3D twin: parented to the cube FLOOR (sits attached to it,
    // inheriting the cube's rotation), then grows toward camera as the faces
    // explode away
    const gltf = await loadGLB(asset('models/compressed/nanotech5000.glb'),
      (f) => this.engine.setProg('nano', f));
    this.model = gltf.scene;
    this.machineRig = new THREE.Group();
    this.group.add(this.machineRig);
    const fit = normalize(this.model, 3.3);   // long machine -> frame wide at FULL size
    // the GLB's bounding box extends ~0.55 local units BELOW the visible feet
    // (stray low geometry inside the main mesh), so bbox-min "seating" hovers
    // the whole machine above the floor. Find the REAL foot level by vertex
    // density: the lowest y where a 2%-height band holds ≥0.3% of samples.
    this.model.updateMatrixWorld(true);
    const rootInv = new THREE.Matrix4().copy(this.model.matrixWorld).invert();
    const _m4 = new THREE.Matrix4(), _vv = new THREE.Vector3();
    const ys = [];
    this.model.traverse((o) => {
      if (o.isMesh && o.geometry && o.geometry.attributes.position) {
        const p = o.geometry.attributes.position;
        const step = Math.max(1, Math.floor(p.count / 4000));
        _m4.multiplyMatrices(rootInv, o.matrixWorld);
        for (let i = 0; i < p.count; i += step) {
          _vv.fromBufferAttribute(p, i).applyMatrix4(_m4);
          ys.push(_vv.y);
        }
      }
    });
    ys.sort((a, b) => a - b);
    const hiY = ys[ys.length - 1];
    const band = (hiY - ys[0]) * 0.02, need = Math.max(8, ys.length * 0.003);
    let seatY = ys[0];
    for (let i = 0, j = 0; i < ys.length; i++) {
      while (j < ys.length && ys[j] <= ys[i] + band) j++;
      if (j - i >= need) { seatY = ys[i]; break; }
    }
    this.machineH = (hiY - seatY) * fit.scale;    // VISIBLE height (bbox is ~27% taller)
    this.model.position.y = -seatY * fit.scale;   // real feet at rig origin = the floor
    this.machineRig.add(this.model);
    this.smallK = 1.1 / 3.3;                  // shrunk to sit inside the 1.35 cube
    this.cubeHf = hf;
    this._qf = new THREE.Quaternion();        // front-face pre-flip temp
    this._qy = new THREE.Quaternion();
    this._up = new THREE.Vector3(0, 1, 0);
    this._va = new THREE.Vector3();
    this._vb = new THREE.Vector3();
    this.model.visible = false;
    // the machine's own embedded animation, scrubbed by scroll after the reveal
    if (gltf.animations && gltf.animations.length) {
      this.mixer = new THREE.AnimationMixer(this.model);
      this.clip = gltf.animations.find((a) => a.name === 'Scene') || gltf.animations[0];
      this.action = this.mixer.clipAction(this.clip);
      this.action.play();
      this.action.paused = true;
    }
  }
  update(t, dt, time, mx, my, raw) {
    if (!this.planes) return;
    // flat grid -> fold into the cube exactly per Ze'ev's AE keyframes (timing
    // from the keys, lerp/slerp between them) -> faces resolve to solid putty
    // -> slow explosion out of frame while the machine grows toward camera
    const u = range(t, 0.26, 0.52);                            // comp timeline, scrubbed by scroll
    const su = smooth(u);
    // the photos move BACK as they fold (cube recedes — no longer huge) and
    // drift LEFT of centre (the text card sits on the right)
    this.cube.position.z = lerp(0, -2.2, su);
    this.cube.position.x = lerp(0, -0.7, su);
    // responsive grid: shrink the 3x2 layout to fit narrow windows (never
    // wider than the tight grid's 4.33 units); eases back to 1 as the cube forms
    const cam = this.engine.camera;
    const availW = 2 * Math.tan(cam.fov * Math.PI / 360) * cam.aspect * (cam.position.z + 0.68) * 0.92;
    const fitK = Math.min(1, availW / 4.35);
    this.cube.scale.setScalar(lerp(fitK, 1, su));
    // cube yaw from Ze'ev's null, CONTINUING once formed — carried through the
    // exit so the machine keeps the rotation of the cube it was born in
    sampleTrack(this.nullTrack, u, null, this.cube.quaternion);
    const extraYaw = Math.max(0, (raw != null ? raw : t) - 0.52) * 0.7;
    this._qy.setFromAxisAngle(this._up, extraYaw);
    this.cube.quaternion.premultiply(this._qy);

    // grid-tightening weight: dies over the FIRST 28% of the fold, before any
    // faces approach each other (unlike 1-su, which lingers and collides)
    const gw = 1 - smooth(range(u, 0, 0.28));
    const ex = smooth(range(t, 0.56, 0.80));    // slow explosion (sides/top)
    // the FLOOR stays put — the machine must be SEEN standing on it while the
    // box opens; it leaves only after the machine lifts off (g from 0.70)
    const exFloor = smooth(range(t, 0.72, 0.90));
    this.planes.forEach((p) => {
      const e = p.userData.role === 'bottom' ? exFloor : ex;
      if (e > 0.001) {
        p.position.copy(p.userData.facePos).addScaledVector(p.userData.flyDir, e * 14);
        p.quaternion.setFromAxisAngle(p.userData.tumbleAxis, e * p.userData.tumbleSpin).multiply(p.userData.faceQuat);
        p.visible = e < 0.999;                  // gone once out of frame — never a fade
      } else {
        sampleTrack(p.userData.track, u, p.position, p.quaternion);
        // the back photo's sweep grazes the already-seated left face mid-fold
        // (both tilted mid-slerp, u≈0.33) — bow its arc 0.12 deeper so it
        // passes cleanly BEHIND; sin bell is zero at both ends, so Ze'ev's AE
        // poses and timing are untouched (verified: no face pair intersects)
        if (p.userData.role === 'back')
          p.position.z -= 0.12 * Math.sin(Math.PI * range(u, 0.10, 0.50));
        // the front photo would seat image-OUT — flip it 180° about its own Y
        // while still parked in the grid (its position doesn't move until
        // u≈0.46), so the flip can't sweep a neighbour; putty back ends outward
        if (p.userData.role === 'front') {
          this._qf.setFromAxisAngle(this._up, Math.PI * smooth(range(u, 0.05, 0.40)));
          p.quaternion.multiply(this._qf);
        }
        if (gw > 0.001) {
          // tight grid: FULL-size images, 0.14 gaps (1/3 of the old 0.417) —
          // pitch 1.489 relaxing to the AE 1.62 (oversizing images instead
          // breaks the cube assembly; verified no face pair intersects)
          p.position.x *= lerp(1, 0.9191, gw);
          p.position.y *= lerp(1, 0.9191, gw);
        }
        p.visible = true;
      }
    });

    // machine: attached to the cube floor while boxed, SEATED on the floor
    // face while the box opens (the floor face doesn't fly until 0.72), then
    // released toward the camera to its full framing — left of centre
    const g = smooth(range(t, 0.70, 0.92));
    this.machineRig.quaternion.copy(this.cube.quaternion);
    this._va.set(0, -this.cubeHf, 0).applyQuaternion(this.cube.quaternion)
      .multiplyScalar(this.cube.scale.x).add(this.cube.position);   // floor point, cube space
    this._vb.set(-0.7, -this.machineH / 2, 0);                       // final framing, left of centre
    this.machineRig.position.lerpVectors(this._va, this._vb, g);
    this.machineRig.scale.setScalar(lerp(this.smallK, 1, g));
    this.model.visible = t > 0.52;              // revealed only once the shell is solid + closed
    // embedded machine animation, scrubbed by the remaining scroll
    if (this.action && this.clip) {
      this.action.time = range(t, 0.58, 0.98) * this.clip.duration;
      this.mixer.update(0);
    }
  }
}

/* ============================================================
   Scene 3 — Symantek simulation / assembly line  (panels 5-6)
   Base line is present; primitive components fly in and snap
   onto it one after another.
   ============================================================ */
class SymantekScene extends Scene {
  async load() {
    const gltf = await loadGLB(asset('models/compressed/symantek.glb'),
      (f) => this.engine.setProg('sym', f));
    this.model = gltf.scene;
    normalize(this.model, 1.9);           // the line's CENTRE piece
    collectMaterials(this.model, this.mats);
    this.group.add(this.model);

    // dummy components extend the line outward on BOTH sides
    this.parts = [];
    const N = 8;
    const rand = mulberry32(7);
    const palette = [COL.accent, COL.brass, COL.ink];
    const spacing = 1.0;
    for (let i = 0; i < N; i++) {
      const side = (i % 2 === 0) ? 1 : -1;
      const tier = Math.floor(i / 2) + 1;        // 1,1,2,2,3,3,4,4
      const slotX = side * tier * spacing;
      let geo;
      const kind = i % 3;
      if (kind === 0) geo = new THREE.BoxGeometry(0.4, 0.4, 0.4);
      else if (kind === 1) geo = new THREE.CylinderGeometry(0.19, 0.19, 0.46, 24);
      else geo = new THREE.ConeGeometry(0.23, 0.46, 24);
      const mat = new THREE.MeshStandardMaterial({
        color: palette[i % 3], metalness: 0.4, roughness: 0.4, transparent: true, opacity: 1,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.userData.slot = new THREE.Vector3(slotX, 0, 0);
      // fly in from its own side, above the line
      mesh.userData.start = new THREE.Vector3(slotX + side * (2.4 + rand()), 2.6 + rand() * 1.2, (rand() - 0.5));
      mesh.userData.win = [0.12 + i * 0.092, 0.12 + i * 0.092 + 0.13];
      mesh.visible = false;
      this.parts.push(mesh);
      this.group.add(mesh);
    }
    // factory floor — short BEHIND the machines, long toward the camera so its
    // front edge runs off the bottom of frame (its sweep wipes the next scene in)
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(26, 14),
      new THREE.MeshStandardMaterial({ color: 0xB8B2A4, roughness: 0.95, metalness: 0 })
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(0, -0.7, 4);
    this.group.add(floor);
    // look down on the floor, and sit the line low (below the text)
    this.group.rotation.x = 0.42;
    this.group.rotation.y = -0.22;
    this.group.position.y = -1.15;
    this.exitH = 7;    // the tilted floor needs extra travel to fully clear the top
    this.enterH = 7;   // and starts fully below, scrolling in from the bottom
  }
  update(t, dt, time) {
    // the more parts on the line, the smaller the whole build gets to stay framed
    const grow = smooth(range(t, 0.12, 0.95));
    this.group.scale.setScalar(lerp(1.0, 0.46, grow));
    this.group.rotation.y = -0.22 + t * 0.12;
    if (!this.parts) return;
    this.parts.forEach((p) => {
      const [a, b] = p.userData.win;
      const k = range(t, a, b);
      if (k <= 0) { p.visible = false; return; }
      p.visible = true;
      const e = smooth(k);
      p.position.lerpVectors(p.userData.start, p.userData.slot, e);
      p.scale.setScalar(e);
      p.material.opacity = e;
      // stays fixed/aligned to the main unit — these are line extensions, not free objects
    });
  }
}

/* ============================================================
   Scene 4 — Minimonster support  (panel 7)
   ============================================================ */
class MinimonsterScene extends Scene {
  async load() {
    const gltf = await loadGLB(asset('models/compressed/minimonster.glb'),
      (f) => this.engine.setProg('mini', f));
    this.model = gltf.scene;
    normalize(this.model, 2.3);
    // the replacement model ships with real materials — keep them as-is; just
    // respect parts the author explicitly flagged not to render
    this.model.traverse((o) => {
      const nm = ((o.name || '') + ' ' + (o.parent ? o.parent.name : '')).toLowerCase();
      if (nm.includes('dont render') || nm.includes("don't render")) o.visible = false;
    });
    this.group.add(this.model);
    this.group.position.x = -0.6;   // sit just left of centre (copy is on the right)
    if (gltf.animations && gltf.animations.length) {
      this.mixer = new THREE.AnimationMixer(this.model);
      this.clip = gltf.animations[0];
      this.action = this.mixer.clipAction(this.clip);
      this.action.play();
      this.action.paused = true;
    }
    // remote-support callouts: numbered markers pinned to REAL parts of the
    // machine with leader lines, appearing one after another like a repair
    // guide (replaces the old meaningless floating pips). ONE color, 1-2-3-4.
    this.pips = [];
    // #1's part (the ATC tool-changer) MOVES with the embedded animation —
    // track the node live so the callout never points at empty air
    this.atcNode = null;
    this.model.traverse((o) => {
      if (!this.atcNode && o.isMesh && (o.name || '').toLowerCase().includes('atc_cover')) this.atcNode = o;
    });
    if (this.atcNode) {
      if (!this.atcNode.geometry.boundingBox) this.atcNode.geometry.computeBoundingBox();
      this.atcCenter = this.atcNode.geometry.boundingBox.getCenter(new THREE.Vector3());
    }
    const anchors = [
      { a: new THREE.Vector3(-0.68, 0.38, -0.02), off: new THREE.Vector3(-0.50, 0.48, 0.25) },  // 1: tool-changer (tracked)
      { a: new THREE.Vector3(-0.09, 0.22, -0.62), off: new THREE.Vector3(1.05, 0.72, 0.45) },   // 2: gantry / X-axis
      { a: new THREE.Vector3(-0.08, -0.40, 0.02), off: new THREE.Vector3(-0.94, 0.35, 0.48) },  // 3: rotary axis + vise
      { a: new THREE.Vector3(-0.09, -0.68, -0.20), off: new THREE.Vector3(1.05, 0.26, 0.60) },  // 4: base frame
    ];
    const rodGeo = new THREE.CylinderGeometry(1, 1, 1, 8);
    anchors.forEach((cfg, i) => {
      const c = document.createElement('canvas'); c.width = c.height = 128;
      const g2 = c.getContext('2d');
      g2.fillStyle = '#C8552E';
      g2.beginPath(); g2.arc(64, 64, 58, 0, Math.PI * 2); g2.fill();
      g2.fillStyle = '#F4F2EE';
      g2.font = '700 62px "Space Grotesk", sans-serif';
      g2.textAlign = 'center'; g2.textBaseline = 'middle';
      g2.fillText(String(i + 1), 64, 67);
      // toneMapped:false — ACES washes the terracotta into pale tan otherwise
      const spr = new THREE.Sprite(new THREE.SpriteMaterial({
        map: new THREE.CanvasTexture(c), transparent: true, opacity: 0, depthWrite: false, toneMapped: false,
      }));
      spr.scale.setScalar(0.001);
      // leader ROD (real mesh — Line linewidth is capped at 1px and too thin)
      const rod = new THREE.Mesh(rodGeo, new THREE.MeshBasicMaterial({
        color: COL.accent, transparent: true, opacity: 0, toneMapped: false,
      }));
      this.group.add(spr);
      this.group.add(rod);
      this.pips.push({ spr, rod, a: cfg.a, off: cfg.off, start: 0.28 + i * 0.14 });
    });
    this._pa = new THREE.Vector3(); this._pb = new THREE.Vector3();
    this._pd = new THREE.Vector3(); this._pu = new THREE.Vector3(0, 1, 0);
    this.group.rotation.y = 0.4;
  }
  update(t, dt, time, mx, my, raw) {
    // scrub the machine animation across the WHOLE viewport passage
    // (enter raw -0.55 .. exit raw 1.58), not just the pinned middle
    const u = range(raw != null ? raw : t, -0.55, 1.45);
    if (this.action && this.clip) { this.action.time = u * this.clip.duration; this.mixer.update(0); }
    this.group.rotation.y = 0.4 - u * 0.9;
    // callouts pop in sequentially (1 -> 4) — a guided remote-support
    // walkthrough. They GROW at full opacity (no fade), the rod extends from
    // the part as the disc grows, and #1 tracks its MOVING part every frame.
    if (this.pips) this.pips.forEach((p, i) => {
      const w = smooth(range(t, p.start, p.start + 0.07));
      if (i === 0 && this.atcNode) {
        this._pa.copy(this.atcCenter);
        this.atcNode.localToWorld(this._pa);
        this.group.worldToLocal(this._pa);
      } else this._pa.copy(p.a);
      this._pb.copy(this._pa).add(p.off);
      p.spr.position.copy(this._pb);
      p.spr.material.opacity = w > 0.001 ? 1 : 0;
      p.spr.scale.setScalar(Math.max(0.001, 0.3 * w));
      // rod from the part, extending toward the disc with the grow — but it
      // STOPS at the disc's rim (running to the centre crosses the digit:
      // the "1" read as a "4")
      this._pd.copy(this._pb).sub(this._pa);
      const full = Math.max(0.001, this._pd.length() - 0.17);
      this._pd.setLength(full * w);
      const len = this._pd.length();
      p.rod.visible = len > 0.01;
      if (p.rod.visible) {
        p.rod.position.copy(this._pa).addScaledVector(this._pd, 0.5);
        p.rod.quaternion.setFromUnitVectors(this._pu, this._pd.normalize());
        p.rod.scale.set(0.013, len, 0.013);
        p.rod.material.opacity = 1;
      }
    });
  }
}

/* ============================================================
   Scene 5 — Forest ON the Earth -> pull back into space (panels 8-11)
   The clearing literally sits on the planet's surface; scrolling
   pulls the camera back so the forest shrinks to a point on the
   whole globe, then the global comms network lights up.
   ============================================================ */
class WorldScene extends Scene {
  async load() {
    this.noRail = true;
    this.R = 1.7;
    this.S0 = 28;                  // start zoom: the earth is SO big its surface reads as nearly flat
    this.spinner = new THREE.Group();
    this.group.add(this.spinner);

    // real smooth-surface earth (geometry-based land/ocean, shiny sea)
    const eg = await loadGLB(asset('models/globe_earth_-_for_3d_printing.glb'), (f) => this.engine.setProg('earth', f));
    this.earth = eg.scene;
    normalize(this.earth, this.R * 2);
    // bring the site — eastern China (~33°N 113°E) — to the patch point (+y);
    // water lies to the east = screen right. Model: north pole +y, front
    // meridian ≈95°E (calibrated against screenshots of the GLB at identity).
    {
      const d = Math.PI / 180, lat = 31.5 * d, dl = (112 - 95) * d;
      const v = new THREE.Vector3(Math.cos(lat) * Math.sin(dl), Math.sin(lat), Math.cos(lat) * Math.cos(dl)).normalize();
      this.earth.quaternion.setFromUnitVectors(v, new THREE.Vector3(0, 1, 0));
    }
    // flat, lighter ocean (the GLB ships a dark metallic sea), and FORCE all
    // earth materials opaque: Sketchfab transparent flags put the land into the
    // transparent render queue, where per-mesh depth sorting against the (also
    // transparent) contiki painted land OVER parts of the rig in narrow scroll
    // bands — the infamous "truncated contiki"
    this.earth.traverse((o) => {
      if (o.isMesh && o.material) {
        o.material.transparent = false;
        o.material.opacity = 1;
        o.material.depthWrite = true;
        if (o.material.color && o.material.color.b > o.material.color.r) {
          o.material.color.set(0x2E5BD8);
          o.material.metalness = 0;
          o.material.roughness = 0.95;
        }
      }
    });
    this.spinner.add(this.earth);

    // space backdrop — fades in as a SUNSET and deepens to black:
    // putty > deep yellow > orange > sunset blue > dark blue > black.
    // toneMapped:false so the ramp colors render exactly (and the putty stop
    // matches the CSS background when the plane first appears).
    this.space = new THREE.Mesh(
      new THREE.PlaneGeometry(140, 140),
      new THREE.MeshBasicMaterial({ color: 0xE6E1D7, transparent: true, opacity: 0, depthWrite: false, toneMapped: false })
    );
    this.space.position.z = -32;
    this.group.add(this.space);
    this.skyStops = [
      [0.00, new THREE.Color(0xE6E1D7)],   // putty
      [0.22, new THREE.Color(0xD9A93B)],   // deep yellow
      [0.45, new THREE.Color(0xD96E30)],   // orange
      [0.66, new THREE.Color(0x46517E)],   // sunset blue
      [0.85, new THREE.Color(0x1B2A4A)],   // dark blue
      [1.00, new THREE.Color(0x0B0F1A)],   // black
    ];

    // --- forest patch on TOP of the earth; camera sees it side-on ---
    // The "forest floor" IS the green part of the earth: a green cap on the
    // sphere. Zoomed in (S0) its curvature reads as a hill rising from the
    // bottom of the frame; pulled back it resolves into the globe's green top.
    const rand = mulberry32(2026);
    this.patchDir = new THREE.Vector3(0, 1, 0);
    const PS = 0.018;                      // patch scale (world units per patch unit = PS * S0 = 0.5)
    this.patch = new THREE.Group();
    this.patch.position.copy(this.patchDir).multiplyScalar(this.R);
    this.patch.scale.setScalar(PS);        // patch local +y is already the surface normal at the pole
    this.spinner.add(this.patch);

    // surface height in patch-local units at local (x, z): RAYCAST the actual
    // relief terrain (the land has hills/coasts — a constant height buries some
    // trees and floats others). Analytic sphere + LAND is only the fallback.
    const LAND = 0.55;
    const groundYSphere = (x, z) => {
      const d2 = (x * x + z * z) * PS * PS;
      return (Math.sqrt(Math.max(0, this.R * this.R - d2)) - this.R) / PS + LAND;
    };
    this.rail.updateMatrixWorld(true);
    const _ray = new THREE.Raycaster();
    // ============================ CRITICAL ============================
    // groundY() raycasts in WORLD space, but load() runs while the engine
    // loop is LIVE: engine.stage carries the MOUSE TILT, re-applied every
    // frame — and load() awaits between settles, so any single up-front
    // neutralization dies at the first await. If the cursor is off-centre
    // when a settle runs, every ray is skewed and the contiki/dozer/trees
    // seat UNDERGROUND on first visit (fine on reload — cursor elsewhere).
    // Therefore EVERY groundY call zeroes the stage tilt, recomputes the
    // matrices, raycasts, and restores. JS is single-threaded: mousemove
    // can't fire inside this call, and the next loop frame re-tilts.
    const groundY = (x, z) => {
      const sr = this.engine.stage.rotation;
      const sx = sr.x, sy = sr.y, sz = sr.z;
      sr.set(0, 0, 0);
      this.engine.stage.updateMatrixWorld(true);
      const o = this.patch.localToWorld(new THREE.Vector3(x, 400, z));
      _ray.set(o, new THREE.Vector3(0, -1, 0));
      const hits = _ray.intersectObject(this.earth, true);
      // lowest NEAR-SURFACE hit = the actual land — the first hit can be one of
      // the globe's baked-in miniature trees (perching content in mid-air), and
      // the ray also exits through the far side of the planet (ignore that)
      let best = null;
      for (const h of hits) {
        const ly = this.patch.worldToLocal(h.point.clone()).y;
        if (ly > -20 && ly < 60 && (best === null || ly < best)) best = ly;
      }
      sr.set(sx, sy, sz);
      return best === null ? groundYSphere(x, z) : best;
    };
    // drop an object so its bounding-box base rests on the HIGHEST terrain under
    // its footprint — better to float a touch on a slope than sink underground
    const settle = (obj, x, z, sink = 0) => {
      obj.position.set(0, 0, 0);
      obj.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(obj);
      const fx = (box.max.x - box.min.x) * 0.4, fz = (box.max.z - box.min.z) * 0.4;
      const samples = [];
      for (let dx = -1; dx <= 1; dx++) for (let dz = -1; dz <= 1; dz++) {
        samples.push(groundY(x + dx * fx, z + dz * fz));
      }
      // a sample that lands on a baked mini-tree reads ~1.5 above its
      // neighbours — discard spikes, then take the highest REAL ground
      const lo = Math.min(...samples);
      let g = lo;
      for (const s of samples) if (s < lo + 0.8) g = Math.max(g, s);
      // the site is a known-flat clearing (siteH ≈ 0) — where every sample hits
      // baked-canopy geometry there is no real ground to read, so clamp
      g = Math.min(g, 0.3);
      obj.position.set(x, g - box.min.y - sink, z);
    };
    // terrain height above the sphere point at the site centre (spinner units) —
    // the anchor in update() must account for it or the whole surface rides high
    this.siteH = groundY(0, 0) * PS;

    const treePaths = ['Tree1', 'Tree8', 'Tree4'];
    const trees = await Promise.all(treePaths.map((n) => loadGLB(asset(`models/compressed/${n}.glb`))));
    trees.forEach((g) => normalize(g.scene, 2.0));
    this.trees = [];
    const COUNT = 12;
    for (let i = 0; i < COUNT; i++) {
      const inst = trees[i % trees.length].scene.clone(true);
      // loose arc around the clearing (ground is nearly flat at this zoom,
      // so depth no longer lifts trees off the distant silhouette)
      const a = Math.PI * (0.95 + (i / (COUNT - 1)) * 1.1) + (rand() - 0.5) * 0.14;
      const rad = 2.8 + rand() * 1.2;
      // shallow arc, shifted toward the camera: deep z puts back trees BEHIND
      // the visible horizon (ship-over-horizon), and during the pull the
      // shrinking sphere brings the tangent line even closer
      const x = Math.cos(a) * rad, z = Math.sin(a) * rad * 0.45 + 0.6;
      inst.scale.setScalar(0.8 + rand() * 0.3);     // full size — they ride up with the hill
      inst.rotation.y = rand() * Math.PI * 2;
      settle(inst, x, z, -0.05);                    // base ON the ground (origin is the model centre!)
      this.trees.push(inst);
      this.patch.add(inst);
    }

    const cg = await loadGLB(asset('models/compressed/contiki.glb'), (f) => this.engine.setProg('contiki', f));
    this.contiki = cg.scene;
    normalize(this.contiki, 7.0);            // hero of the clearing
    this.contiki.rotation.y = Math.PI * 0.5 + 0.5;   // angled across the clearing (per AE ref)
    settle(this.contiki, -0.5, 0.9, -0.15);  // floats a touch — better than burying the booms
    this.contikiMats = [];
    this.contiki.traverse((o) => {
      if (o.isMesh) {
        const nm = ((o.name || '') + ' ' + (o.parent ? o.parent.name : '')).toLowerCase();
        let col = 0x9AA0A6, met = 0.6, rough = 0.4;
        if (nm.includes('container')) { col = 0x5E7A4E; met = 0.2; rough = 0.7; }
        else if (nm.includes('electric')) { col = 0x32363C; met = 0.5; rough = 0.5; }
        o.material = new THREE.MeshStandardMaterial({ color: col, metalness: met, roughness: rough, transparent: true, opacity: 0 });
        this.contikiMats.push(o.material);
      }
    });
    this.contiki.visible = false;
    this.patch.add(this.contiki);

    // bulldozer waits in the clearing (keeps its own materials)
    const bz = await loadGLB(asset('models/Bulldozer.glb'));
    this.dozer = bz.scene;
    normalize(this.dozer, 0.78);
    settle(this.dozer, 0.8, 3.2, -0.30);     // ride high — floating beats buried
    this.dozerLift = this.dozer.position.y - groundY(0.8, 3.2);
    // precompute terrain heights around the figure-8 (the loop crosses ground
    // that dips toward the coast — a fixed height buries the dozer)
    this.dozerPath = [];
    for (let i = 0; i < 64; i++) {
      const u = (i / 64) * Math.PI * 2;
      const g = Math.min(groundY(0.8 + Math.sin(u) * 2.2, 3.2 + Math.sin(u * 2) * 0.9), 0.3);
      this.dozerPath.push(g + this.dozerLift);
    }
    this.patch.add(this.dozer);

    // the Draco-compressed rigs ship unreliable bounding data — per-mesh frustum
    // culling truncates the contiki at certain scroll angles. Disable it for the
    // clearing content (a handful of meshes, negligible cost).
    this.patch.traverse((o) => { if (o.isMesh) o.frustumCulled = false; });

    // --- global demo nodes + dashed comms arcs (revealed once in space) ---
    this.glyphMats = [];
    this.nodes = [this.patchDir.clone()];          // the forest clearing is node 0
    for (let i = 0; i < 8; i++) {
      const lat = (rand() - 0.5) * Math.PI * 0.8, lon = rand() * Math.PI * 2;
      this.nodes.push(new THREE.Vector3(Math.cos(lat) * Math.cos(lon), Math.sin(lat), Math.cos(lat) * Math.sin(lon)));
    }
    // node glyphs SIT directly on the surface (no tether beams): base sunk a
    // touch into the ground, cones pointing UP (away from the surface).
    // Node 0 is the forest clearing itself — no marker, the site IS the node.
    this.nodes.forEach((dir, i) => {
      if (i === 0) return;                          // forest node has no glyph
      const k = i % 3; let geo, half;
      if (k === 0) { geo = new THREE.BoxGeometry(0.14, 0.14, 0.14); half = 0.07; }
      else if (k === 1) { geo = new THREE.CylinderGeometry(0.07, 0.07, 0.18, 12); half = 0.09; }
      else { geo = new THREE.ConeGeometry(0.09, 0.18, 12); half = 0.09; }
      const m = new THREE.MeshStandardMaterial({ color: COL.accent, emissive: COL.accent, emissiveIntensity: 0.6, metalness: 0.3, roughness: 0.4, transparent: true, opacity: 0 });
      this.glyphMats.push(m);
      const g = new THREE.Mesh(geo, m);
      g.position.copy(dir).multiplyScalar(this.R + half - 0.02);
      g.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
      this.spinner.add(g);
    });
    this.arcs = []; this.arcMats = [];
    const SEG = 48;
    for (let i = 0; i < this.nodes.length; i++) {
      const a = this.nodes[i], b = this.nodes[(i + 3) % this.nodes.length];
      if (a.distanceTo(b) < 0.5) continue;
      const pts = [];
      for (let s = 0; s <= SEG; s++) { const f = s / SEG; pts.push(a.clone().lerp(b, f).normalize().multiplyScalar(this.R * (1 + 0.22 * Math.sin(f * Math.PI)))); }
      // fat dashed arc (Line2 — plain Line linewidth is capped at 1px)
      const geo = new LineGeometry();
      geo.setPositions(pts.flatMap((p) => [p.x, p.y, p.z]));
      const mat = new LineMaterial({
        color: COL.accent, linewidth: 2.5, dashed: true, dashSize: 0.08, gapSize: 0.06,
        transparent: true, opacity: 0,
      });
      mat.resolution.set(window.innerWidth, window.innerHeight);
      const line = new Line2(geo, mat); line.computeLineDistances();
      line.visible = false;   // hard-gated by reveal: LineMaterial's opacity
                              // uniform unreliably leaves a white ghost arc
      this.spinner.add(line); this.arcMats.push(mat);
      if (!this.arcLines) this.arcLines = [];
      this.arcLines.push(line);
      // comm packets: SMALL, tight, fast sprites — industrial data deployment,
      // not will-o'-the-wisps. ONE shared material; two sprites per arc
      // traveling in OPPOSITE directions = two-way communication.
      if (!this._pulseMat) {
        const c = document.createElement('canvas'); c.width = c.height = 64;
        const g2 = c.getContext('2d');
        const grd = g2.createRadialGradient(32, 32, 0, 32, 32, 32);
        grd.addColorStop(0.0, 'rgba(255,255,255,1)');
        grd.addColorStop(0.30, 'rgba(185,218,255,0.85)');
        grd.addColorStop(0.60, 'rgba(80,145,255,0.15)');
        grd.addColorStop(1.0, 'rgba(80,145,255,0)');
        g2.fillStyle = grd; g2.fillRect(0, 0, 64, 64);
        this._pulseMat = new THREE.SpriteMaterial({
          map: new THREE.CanvasTexture(c), transparent: true, opacity: 0,
          blending: THREE.AdditiveBlending, depthWrite: false,
        });
      }
      const mkPulse = () => {
        const s = new THREE.Sprite(this._pulseMat);
        s.scale.setScalar(0.055);
        this.spinner.add(s);
        return s;
      };
      this.arcs.push({ pts, pulse: mkPulse(), pulse2: mkPulse(), phase: rand(), phase2: rand() });
    }

    // end-frame wordmark: SHIVOO RISES from behind the globe (t 0.92+) and
    // settles, ~half the globe's final diameter. In this.group (NOT spinner)
    // so it neither spins nor tilts.
    {
      const c = document.createElement('canvas'); c.width = 1024; c.height = 256;
      const g2 = c.getContext('2d');
      // drawWord(sp): letter-spacing is ANIMATED (4px -> 44px as it rises);
      // THIN weight (300), no period. Font size is fitted at FULL spread so
      // the settled width is exact and early frames are narrower.
      const drawWord = (sp) => {
        g2.clearRect(0, 0, 1024, 256);
        g2.textAlign = 'center'; g2.textBaseline = 'middle';
        const hasSp = 'letterSpacing' in g2;
        if (hasSp) g2.letterSpacing = '44px';
        g2.font = '300 150px "Space Grotesk", sans-serif';
        const w0 = g2.measureText('SHIVOO').width;
        const fs = Math.floor(150 * 940 / w0);
        g2.font = `300 ${fs}px "Space Grotesk", sans-serif`;
        if (hasSp) g2.letterSpacing = sp.toFixed(1) + 'px';
        g2.fillStyle = '#F4F2EE';
        g2.fillText('SHIVOO', 512, 130);
        this.wordSp = sp;
      };
      this.drawWord = drawWord;
      this.wordTex = new THREE.CanvasTexture(c);
      drawWord(4);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => { drawWord(this.wordSp); this.wordTex.needsUpdate = true; });
      }
      this.word = new THREE.Mesh(
        new THREE.PlaneGeometry(1.5, 1.5 * 256 / 1024),
        new THREE.MeshBasicMaterial({ map: this.wordTex, transparent: true, opacity: 0, toneMapped: false, depthWrite: false })
      );
      // z -0.55: BEHIND the globe, so the rise emerges from behind its limb
      this.word.position.set(0, 0.68, -0.55);
      this.word.scale.setScalar(0.7);
      this.group.add(this.word);
    }
    this.headerEl = document.querySelector('.site-header');
  }
  update(t, dt, time, mx, my, raw) {
    // the ground scrolls up from the bottom as the minimonster exits (mm is
    // fully out at world raw ≈ +0.03 — section math: mm_raw = (300+480*raw)/200,
    // rail fully up at mm_raw 1.58). The enter is intentionally SHALLOW and
    // EARLY (-4.5, window 0.0-0.18): Ze'ev wants half the old whitespace, and
    // the earth/trees peeking from the bottom during the handoff is fine — the
    // motion is continuous from fully-hidden at raw 0 (tilt 0 + lift -4.5
    // keeps the zenith site at -5.5, under the frame), so nothing cuts in
    // the earth moves TOGETHER with the minimonster — when the mm rail moves,
    // the earth moves; when the mm is pinned, the earth holds. Two ramps that
    // MIRROR mm's rail windows (mm_raw = (300+480·raw)/200): mm enters
    // mm_raw -0.55..0.10 = world raw -0.85..-0.58 (earth rises half way),
    // mm pinned -0.58..-0.25 (earth HOLDS), mm exits -0.25..0.03 (earth
    // completes the rise). A single continuous ramp = "earth moves while the
    // MM is STILL", rejected twice.
    // 0.25/0.75 split: while the mm is pinned the earth PARKS as a low sliver
    // near the bottom edge (0.5/0.5 parked it too high mid-frame)
    const enterP = 0.25 * smooth(range(raw, -0.85, -0.58))
                 + 0.75 * smooth(range(raw, -0.25, 0.05));
    this.group.position.y = lerp(-4.5, 0, enterP);

    // pull back: clearing on the surface (close) -> whole globe in space.
    // The site swings toward the camera as we leave (the video ends facing
    // Asia, site upper-right), then the globe recedes and spins westward.
    const pull = smooth(range(t, 0.46, 0.80));
    const recede = smooth(range(t, 0.82, 1.0));
    // "materialize" move while the contiki fades in: a CENTRED zoom-in (the
    // site anchor below keeps the clearing fixed in frame, so growing S reads
    // as moving in), which the pull-out then SLOWLY reverses. NO yaw here —
    // any yaw drift pans the clearing sideways (looked like a move-left, not
    // a zoom). The zoom keeps going past contiki-100% (fade ends 0.38, ramp
    // runs to 0.50) and only unwinds as the delayed pull (0.46+) takes over.
    const matM = smooth(range(t, 0.22, 0.50)) * (1 - pull);
    const S = lerp(this.S0, 1.0, pull) * lerp(1, 0.6, recede) * (1 + 0.58 * matM);
    this.spinner.scale.setScalar(S);
    // base tilt 0.34, NOT 0.26: the sightline at 0.26 grazes the relief — a
    // small swell between camera and clearing occludes every base (contiki/
    // dozer/trees read as "underground" even though settle() seats them
    // correctly). Final tilt unchanged: 0.34 + 0.72 = 1.06.
    const tilt = (0.34 + smooth(range(t, 0.55, 0.90)) * 0.72) * enterP;
    // once receded, the globe keeps turning slowly on its own (footer idle
    // spin). MUST reset + fade with recede: leftover yaw combined with the
    // tilt swings the site sideways when scrolling back up to the clearing
    this.idleYaw = recede > 0.01 ? (this.idleYaw || 0) + dt * 0.06 * recede : 0;
    _wq1.setFromAxisAngle(_wx, tilt);
    _wq2.setFromAxisAngle(_wy, (1.2 + this.idleYaw) * recede);
    this.spinner.quaternion.copy(_wq1).premultiply(_wq2);
    // anchor the SITE SURFACE while grounded/pulling (it climbs low -> high in
    // frame), then hand off to a fixed globe centre for the recede.
    // R + siteH = sphere radius PLUS the relief terrain height at the site.
    const dy = Math.cos(tilt), dz = Math.sin(tilt);
    const Rs = this.R + (this.siteH || 0);
    // clearing lands HIGHER in frame (-0.60, was -1.05) and a touch right
    const py = lerp(-0.60, 1.0, pull);
    _wv1.set(lerp(0.45, 0, pull), py - Rs * S * dy, -Rs * S * dz);   // site-surface-anchored
    _wv2.set(0, -0.12, 0);                          // centre-anchored
    this.spinner.position.lerpVectors(_wv1, _wv2, smooth(range(t, 0.74, 0.88)));

    if (this.space) {
      // sunset ramp: opacity arrives fast (while the color is still putty, so
      // nothing pops), then the color walks the stops as we leave the surface
      this.space.material.opacity = smooth(range(t, 0.46, 0.56));
      const sp = smooth(range(t, 0.48, 0.88));
      const st = this.skyStops;
      for (let i = 1; i < st.length; i++) {
        if (sp <= st[i][0] || i === st.length - 1) {
          const f = range(sp, st[i - 1][0], st[i][0]);
          this.space.material.color.lerpColors(st[i - 1][1], st[i][1], f);
          break;
        }
      }
    }

    // the bulldozer drives a lazy figure-8 in front of the contiki (by request —
    // one of the two idle-motion exceptions, along with the footer globe spin)
    if (this.dozer && this.dozerPath) {
      const u = time * 0.15;
      const f = ((u / (Math.PI * 2)) % 1 + 1) % 1 * 64;
      const i0 = Math.floor(f) % 64, i1 = (i0 + 1) % 64;
      this.dozer.position.x = 0.8 + Math.sin(u) * 2.2;
      this.dozer.position.z = 3.2 + Math.sin(u * 2) * 0.9;
      this.dozer.position.y = lerp(this.dozerPath[i0], this.dozerPath[i1], f - Math.floor(f));
      this.dozer.rotation.y = Math.atan2(Math.cos(u) * 2.2, Math.cos(u * 2) * 1.8) + Math.PI / 2;
    }

    // contiki fades in (opacity) only AFTER the forest has fully landed —
    // enterP completes at raw 0.18; while the scene is still lifting/tilting
    // the horizon tangent cuts through the clearing and a mid-fade contiki
    // gets flat-cut by the sphere's limb (truncation, any aspect ratio).
    // Transparent ONLY while fading — a permanently-transparent rig
    // depth-sorts per-mesh and loses to other transparent objects.
    const ce = smooth(range(t, 0.24, 0.38));
    if (this.contikiMats) this.contikiMats.forEach((m) => { m.opacity = ce; m.transparent = ce < 0.999; });
    if (this.contiki) this.contiki.visible = ce > 0.01;

    // wordmark FADES + RISES from behind the globe — slow and dramatic
    // (t 0.88-0.99, starting as the last card fades at 0.88): it grows
    // (scale 0.7 -> 1), the letters stretch apart (4px -> 44px tracking),
    // easeOutBack on the rise so it overshoots a touch and SETTLES
    if (this.word) {
      const wr = smooth(range(t, 0.88, 0.99));
      this.word.material.opacity = smooth(range(t, 0.88, 0.94));
      const k = wr - 1;
      const ob = 1 + 2.7 * k * k * k + 1.7 * k * k;   // easeOutBack
      this.word.position.y = lerp(0.68, 1.58, ob);
      this.word.scale.setScalar(lerp(0.7, 1, wr));
      const sp = lerp(4, 44, wr);
      if (this.drawWord && Math.abs(sp - this.wordSp) > 0.25) {
        this.drawWord(sp);
        this.wordTex.needsUpdate = true;
      }
    }
    if (this.headerEl) {
      // dark from the sunset-blue stop onward, back to ink once the opaque
      // specs section scrolls over the header (world raw ≈ 1.18)
      const dark = t > 0.64 && raw < 1.18;
      if (dark !== this._hdrDark) { this._hdrDark = dark; this.headerEl.classList.toggle('on-dark', dark); }
    }

    const reveal = smooth(range(t, 0.68, 0.92));
    if (this.glyphMats) this.glyphMats.forEach((m) => { m.opacity = reveal; });
    if (this.arcs) {
      if (this.arcLines) this.arcLines.forEach((l) => { l.visible = reveal > 0.02; });
      this.arcMats.forEach((m) => { m.opacity = 0.75 * reveal; });
      // TIME-driven (keeps flowing during the footer idle spin) and lerped
      // between polyline points — fast small packets, both directions per arc
      this._pulseMat.opacity = reveal * 0.95;
      this.arcs.forEach((arc) => {
        const place = (s, f) => {
          const fi = f * (arc.pts.length - 1);
          const i0 = Math.floor(fi);
          s.position.lerpVectors(arc.pts[i0], arc.pts[Math.min(arc.pts.length - 1, i0 + 1)], fi - i0);
        };
        place(arc.pulse, ((time * 0.32) + arc.phase) % 1);
        place(arc.pulse2, 1 - (((time * 0.27) + arc.phase2) % 1));
      });
    }
  }
}

/* ============================================================
   Engine
   ============================================================ */
class Engine {
  constructor() {
    this.canvas = document.getElementById('bg-canvas');
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.camera = new THREE.PerspectiveCamera(42, window.innerWidth / window.innerHeight, 0.1, 100);
    this.camera.position.set(0, 0, 6.4);

    this.world = new THREE.Scene();
    this.addLights();
    this.stage = new THREE.Group();   // holds every scene rail; tilts with the mouse
    this.world.add(this.stage);

    this.clock = new THREE.Clock();
    this.mx = 0; this.my = 0; this.tmx = 0; this.tmy = 0;

    // loader progress
    this.prog = {};
    this.criticalKeys = ['arm', 'nano', 'img0', 'img1', 'img2', 'img3', 'img4', 'img5'];
    this.weights = { arm: 1, nano: 3 };   // others default 0.25
    this.loaderEl = document.getElementById('loader');
    this.fillEl = document.getElementById('loader-progress-fill');
    this.pctEl = document.getElementById('loader-percentage-num');
    this.railEl = document.getElementById('scroll-rail-fill');
    this.loaderDone = false;

    this.scenes = [];
    this.bindEvents();
  }

  addLights() {
    this.world.add(new THREE.AmbientLight(COL.warm, 0.75));
    const key = new THREE.DirectionalLight(0xffffff, 2.6);
    key.position.set(5, 8, 6);
    this.world.add(key);
    const fill = new THREE.DirectionalLight(COL.cool, 1.0);
    fill.position.set(-6, 3, -4);
    this.world.add(fill);
    const rim = new THREE.PointLight(COL.accent, 1.6, 16);
    rim.position.set(-2, 1.5, 3);
    this.world.add(rim);
  }

  register(scene) { this.scenes.push(scene); }

  setProg(key, frac) {
    this.prog[key] = clamp(frac, 0, 1);
    if (!this.loaderDone) this.updateLoader();
  }
  updateLoader() {
    let sum = 0, total = 0;
    for (const k of this.criticalKeys) {
      const w = this.weights[k] || 0.25;
      total += w;
      sum += (this.prog[k] || 0) * w;
    }
    const pct = Math.min(100, Math.round((sum / total) * 100));
    if (this.fillEl) this.fillEl.style.width = pct + '%';
    if (this.pctEl) this.pctEl.textContent = pct;
    if (pct >= 100 && !this.loaderDone) {
      this.loaderDone = true;
      setTimeout(() => this.loaderEl.classList.add('is-loaded'), 400);
    }
  }

  bindEvents() {
    window.addEventListener('mousemove', (e) => {
      this.tmx = (e.clientX / window.innerWidth) * 2 - 1;
      this.tmy = -(e.clientY / window.innerHeight) * 2 + 1;
    });
    window.addEventListener('resize', () => {
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(window.innerWidth, window.innerHeight);
    });
  }

  // raw scroll progress of a section: <0 before pin, 0..1 during pin, >1 after
  sectionRaw(section) {
    const rect = section.getBoundingClientRect();
    const scrollable = rect.height - window.innerHeight;
    if (scrollable <= 0) return rect.top < 0 ? 1 : 0;
    return -rect.top / scrollable;
  }

  // one card per overlay: slides in for its beat-span, beats cross-fade inside.
  // Text NEVER fades during the card's slide — the first beat is held active
  // before its window and the last beat after; fades happen only BETWEEN beats.
  updateOverlay(section, raw) {
    const t = clamp(raw, 0, 1);
    section.querySelectorAll('.scene-card').forEach((card) => {
      const beats = Array.from(card.querySelectorAll('.beat'));
      const spans = beats.map((b) => (b.dataset.range || '0,0').split(',').map(Number));
      const lo = Math.min(...spans.map((s) => s[0]));
      const hi = Math.max(...spans.map((s) => s[1]));
      // exactly ONE beat is active at every t: each owns the span up to the
      // midpoint between its end and the next beat's start, so the fade-out
      // and the next fade-in fire at the SAME moment — the card is never
      // blank for longer than the transition itself
      beats.forEach((b, i) => {
        const from = i === 0 ? -Infinity : (spans[i - 1][1] + spans[i][0]) / 2;
        const to = i === beats.length - 1 ? Infinity : (spans[i][1] + spans[i + 1][0]) / 2;
        b.classList.toggle('is-active', t >= from && t < to);
      });
      // cards with data-fade-out exit by FADING in place at the given t
      // (no slide-out) — used by the last space card so the rising SHIVOO
      // wordmark gets a clean stage
      const fadeAt = card.dataset.fadeOut ? parseFloat(card.dataset.fadeOut) : null;
      const inWin = raw >= lo - 0.05 && (fadeAt !== null || raw <= hi + 0.06);
      card.classList.toggle('is-in', inWin);
      if (fadeAt !== null) card.classList.toggle('is-faded', t >= fadeAt);
    });
  }

  start() { this.clock.start(); this.loop(); }

  loop() {
    requestAnimationFrame(() => this.loop());
    const dt = Math.min(this.clock.getDelta(), 0.05);
    const time = this.clock.elapsedTime;

    // global reactive tilt of the whole 3D stage with the mouse
    this.mx += (this.tmx - this.mx) * 0.05;
    this.my += (this.tmy - this.my) * 0.05;
    this.stage.rotation.y = this.mx * 0.20;
    this.stage.rotation.x = -this.my * 0.14;

    // top scroll-progress bar
    const docH = document.documentElement.scrollHeight - window.innerHeight;
    if (this.railEl) this.railEl.style.width = (clamp(window.scrollY / docH, 0, 1) * 100) + '%';

    const H = 3.7;   // short travel so scenes overlap and hand off with almost no gap
    for (const sc of this.scenes) {
      const raw = this.sectionRaw(sc.section);
      // noRail scenes (the world) hide below the frame via their enter lift, so
      // they can mount/preload EARLY (the 8MB globe must never pop in mid-view),
      // and they stay mounted to the page bottom (idle globe spin at the footer)
      const near = raw > (sc.noRail ? -1.4 : -0.6) && raw < (sc.noRail ? 2.5 : 1.6);
      if (!near) { sc.rail.visible = false; continue; }
      sc.ensureLoaded();

      // continuous reel: rise from below, hold centred, slide up and off the top.
      // a scene can opt to start entering earlier / from a smaller offset (the arm
      // peeks just under the hero fold and rises as the hero scrolls off).
      // enter and exit windows are long and overlap the neighbouring section so
      // one model is always sliding in as the previous slides out (no dead gap)
      const enterFrom = sc.enterFrom != null ? sc.enterFrom : -0.55;
      const enterH = sc.enterH != null ? sc.enterH : H;
      const exitH = sc.exitH != null ? sc.exitH : H;   // big scenes can ask for more exit travel
      let railY = 0;
      if (!sc.noRail) {
        if (raw < 0.10) railY = lerp(-enterH, 0, smooth(range(raw, enterFrom, 0.10)));
        else if (raw > 0.90) railY = lerp(0, exitH, smooth(range(raw, 0.90, 1.58)));
      }
      sc.rail.position.y = railY;
      // visibility must respect each direction's own travel: enter depth is
      // enterH (negative railY), exit height is exitH — using exitH for both
      // popped deep-entering scenes (nanotech grid) into view mid-rise
      sc.rail.visible = sc.loaded && railY > -enterH * 1.1 && railY < exitH * 1.1;

      if (sc.rail.visible) sc.update(clamp(raw, 0, 1), dt, time, this.mx, this.my, raw);
      this.updateOverlay(sc.section, raw);
    }

    this.renderer.render(this.world, this.camera);
  }
}

/* ============================================================
   Boot
   ============================================================ */
const engine = new Engine();
engine.register(new ArmScene(engine, 'section-arm'));
engine.register(new NanotechScene(engine, 'section-nanotech'));
engine.register(new SymantekScene(engine, 'section-symantek'));
engine.register(new MinimonsterScene(engine, 'section-minimonster'));
const worldScene = new WorldScene(engine, 'section-world');
worldScene.noRail = true;   // must be set BEFORE load so the early preload gate applies
engine.register(worldScene);

// preload the critical (above-the-fold-ish) assets immediately
engine.scenes[0].ensureLoaded();
engine.scenes[1].ensureLoaded();

// safety: never let the loader trap the user if a network asset stalls
setTimeout(() => { if (!engine.loaderDone) { engine.loaderDone = true; engine.loaderEl.classList.add('is-loaded'); } }, 12000);

engine.start();
window.__engine = engine;   // TEMP debug handle — remove before ship

/* ---------- CTA behaviour ---------- */
const headerCta = document.getElementById('header-cta-btn');
const footerCta = document.getElementById('footer-cta-link');
function scrollToContact(e) {
  if (e) e.preventDefault();
  document.getElementById('section-specs').scrollIntoView({ behavior: 'smooth' });
}
if (headerCta) headerCta.addEventListener('click', scrollToContact);
if (footerCta) footerCta.addEventListener('click', scrollToContact);
